const decodeFormas = (data, precision, numeroPuntos) => {
  const final = [];
  let d = parseFloat(data.substring(0, 4));

  const firstElement = d / precision;
  final.push(firstElement);

  const forma = data.match(/.{1,2}/g);
  forma.splice(0, 2);
  if (forma.length > numeroPuntos) {
    for (let i = 0; i < numeroPuntos - 1; i++) {
      const y = forma[i];
      let x = parseInt(y, 16) - 127.0;
      d += x;
      x = d / precision;

      final.push(x);
    }
  }

  return final;
};

/**
 * Convierte un array de pares ángulo/radio al formato FormaRO
 * @param {Array<{angle: number, radius: number}>} angleRadiusPairs - Array de pares ángulo/radio
 * @param {number} numPoints - Número de puntos en el formato FormaRO (típicamente 600)
 * @param {number} precision - Precisión para la conversión (por defecto 100)
 * @returns {string} Formato FormaRO
 */
const convertAngleRadiusToForma = (angleRadiusPairs, numPoints = 600, precision = 100) => {
  // Ordenamos los pares por ángulo
  const sortedPairs = [...angleRadiusPairs].sort((a, b) => a.angle - b.angle);

  // Creamos array de radios interpolados
  const radios = new Array(numPoints);
  const angleStep = 360 / numPoints;

  // Interpolamos los radios para cada ángulo
  for (let i = 0; i < numPoints; i++) {
    const currentAngle = i * angleStep;

    // Encontrar los puntos de referencia más cercanos
    const prevPoint =
      sortedPairs.findLast((p) => p.Angulo <= currentAngle) || sortedPairs[sortedPairs.length - 1];
    const nextPoint = sortedPairs.find((p) => p.Angulo > currentAngle) || sortedPairs[0];

    // Interpolación lineal
    if (prevPoint === nextPoint) {
      radios[i] = prevPoint.Radio;
    } else {
      const ratio = (currentAngle - prevPoint.Angulo) / (nextPoint.Angulo - prevPoint.Angulo);
      radios[i] = prevPoint.Radio + (nextPoint.Radio - prevPoint.Radio) * ratio;
    }
  }

  // Convertir a formato FormaRO
  const firstRadius = Math.round(radios[0] * precision);
  let formaRO = firstRadius.toString().padStart(4, "0");

  // Calcular diferencias y convertir a hex
  for (let i = 1; i < numPoints; i++) {
    const currentRadius = Math.round(radios[i] * precision);
    const previousRadius = Math.round(radios[i - 1] * precision);
    const diff = currentRadius - previousRadius;

    // La diferencia debe estar en el rango [-127, 128] y sumamos 127
    const adjusted = Math.max(-127, Math.min(128, diff)) + 127;
    const hex = adjusted.toString(16).padStart(2, "0");
    formaRO += hex;
  }

  return formaRO;
};

/**
 * Simetría de 600 puntos
 *
 * @param {Array<number>} radios - Array de radios
 * @returns {Array<number>} Array de radios simétricos
 * @date 2024-11-21
 */
const simetria600Puntos = (radios) => {
  const simetria = new Array(600);

  for (let i = 0; i <= 300; i++) {
    simetria[i] = radios[300 - i];
  }
  for (let i = 301; i <= 599; i++) {
    simetria[i] = radios[599 - i + 301];
  }

  return simetria;
};

/**
 * Interpolación de radios para obtener una distribución equitativa de puntos
 *
 * FUNCION HEREDADA DE VB.NET
 *
 * @param {Array<number>} radios - Array de radios
 * @param {Array<number>} angulos - Array de ángulos
 * @returns {Array<number>} Array de radios interpolados
 * @date 2024-11-21
 */
const setEqDist = (radios, angulos) => {
  const radiosEQ = new Array(600);

  for (let i = 0; i < 600; i++) {
    const anguloDeg = i * (360 / 600);
    const anguloRad = i * ((Math.PI * 2) / 600);

    let j = 0;
    while (angulos[j] < anguloDeg && j < 599) {
      j++;
    }

    let anguloPosterior, radioPosterior;
    if (angulos[j] < anguloDeg) {
      anguloPosterior = angulos[0] + 360;
      radioPosterior = radios[0];
    } else {
      anguloPosterior = angulos[j];
      radioPosterior = radios[j];
    }

    const anguloAnterior = j !== 0 ? angulos[j - 1] : angulos[599];
    const radioAnterior = j !== 0 ? radios[j - 1] : radios[599];

    if (anguloPosterior !== anguloAnterior) {
      const cercaniaAlPosterior =
        1 - Math.abs((anguloPosterior - anguloDeg) / (anguloPosterior - anguloAnterior));
      const cercaniaAlAnterior =
        1 - Math.abs((anguloAnterior - anguloDeg) / (anguloPosterior - anguloAnterior));
      radiosEQ[i] = radioAnterior * cercaniaAlAnterior + radioPosterior * cercaniaAlPosterior;
    }
  }

  return radiosEQ;
};

/**
 * Convierte un array de puntos a un string en formato FormaRO
 *
 * FUNCION HEREDADA DE VB.NET
 *
 * @param {Array<number>} puntos - Array de puntos
 * @returns {string} String en formato FormaRO
 * @date 2024-11-21
 */
const getStringFromPuntos = (puntos) => {
  const precision = 100;
  let resultado = "";

  // Primer valor
  let valAnterior = Math.round(precision * puntos[0]);
  resultado += valAnterior.toString().padStart(4, "0");

  // Resto de valores
  for (let i = 1; i < puntos.length; i++) {
    const val = Math.round(precision * puntos[i]);
    const diff = 127 + val - valAnterior;
    resultado += diff.toString(16).padStart(2, "0").toUpperCase();
    valAnterior = val;
  }

  return resultado;
};

/**
 * Convierte los datos de ángulos y radios a formato FormaRO
 *
 * FUNCION HEREDADA DE VB.NET
 *
 * @param {Array<{Angulo: number, Radio: number}>} muestras - Array de pares ángulo/radio
 * @param {boolean} ojoIzquierdo - Indica si es el ojo izquierdo
 * @returns {Array<Object>} Array con las formas de ambos ojos
 * @date 2024-11-21
 */
const convertirForma = (muestras, ojoIzquierdo = false) => {
  const radios = muestras.map((m) => Math.min(m.Radio, 200));
  const angulos = muestras.map((m) => m.Angulo);

  // Calcular dimensiones
  let minX = 0,
    maxX = 0,
    minY = 0,
    maxY = 0;

  for (let i = 0; i < radios.length; i++) {
    const pX = radios[i] * Math.cos((Math.PI * angulos[i]) / 180);
    const pY = radios[i] * Math.sin((Math.PI * angulos[i]) / 180);

    maxX = Math.max(maxX, pX);
    minX = Math.min(minX, pX);
    maxY = Math.max(maxY, pY);
    minY = Math.min(minY, pY);
  }

  // ANCHURABOX = anchura horizontal (ΔX), ALTURABOX = altura vertical (ΔY)
  const anchuraBox = Math.round((maxX - minX) * 100) / 100;
  const alturaBox = Math.round((maxY - minY) * 100) / 100;

  // Convertir a puntos equidistantes
  const radiosEq = setEqDist(radios, angulos);

  // Crear objetos de forma
  const formas = [
    {
      LENTE: ojoIzquierdo ? "I" : "D",
      NUMPUNTOS: radios.length,
      ZPRECISION: 100,
      TIPOPUNTOS: "R",
      PUNTOS: getStringFromPuntos(radiosEq),
      ORIGEN: "F",
      ANCHURABOX: anchuraBox.toString(),
      ALTURABOX: alturaBox.toString(),
    },
    {
      LENTE: ojoIzquierdo ? "D" : "I",
      NUMPUNTOS: radios.length,
      ZPRECISION: 100,
      TIPOPUNTOS: "R",
      PUNTOS: getStringFromPuntos(simetria600Puntos(radiosEq)),
      ORIGEN: "F",
      ANCHURABOX: anchuraBox.toString(),
      ALTURABOX: alturaBox.toString(),
    },
  ];

  return formas;
};

export {
  decodeFormas,
  convertAngleRadiusToForma,
  simetria600Puntos,
  setEqDist,
  getStringFromPuntos,
  convertirForma,
};
